/**
 * "How to play" panel explaining BUY/KEEP/SELL/AVOID. Collapsible —
 * defaults to expanded for first-time visitors, collapsed after they've
 * dismissed it (persisted in localStorage).
 */
import React, { useState } from "react";
import { CaretDown, CaretRight, Info } from "@phosphor-icons/react";

const DECISIONS = [
    {
        key: "BUY",
        color: "text-fa-mint border-fa-mint/50",
        title: "BUY",
        body: "You're deciding to bring them into your team. You're also recommending others pick them up for this round.",
    },
    {
        key: "KEEP",
        color: "text-fa-yellow border-fa-yellow/50",
        title: "KEEP",
        body: "You already own them and you're holding. Classic hold — you trust the points are coming.",
    },
    {
        key: "SELL",
        color: "text-[#F08080] border-[#F08080]/60",
        title: "SELL",
        body: "You used to own them but you're cashing out — or, for highly-owned (HOT) players, you're flagging \"let the crowd hold the burden, pivot to a cheap differential\".",
    },
    {
        key: "AVOID",
        color: "text-white/60 border-white/30",
        title: "AVOID",
        body: "Steer clear entirely. Too expensive, ugly fixtures, injury risk, or rotation concerns.",
    },
];

const STORAGE_KEY = "wc_round_call_help_collapsed";

export default function HelpPanel({ deadline }) {
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
    });
    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    };

    return (
        <section className="border border-fa-yellow/30 bg-[linear-gradient(135deg,#1a1606,#0d0d0d)]" data-testid="help-panel">
            <button
                type="button"
                onClick={toggle}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/[0.02]"
                data-testid="help-toggle"
            >
                <div className="flex items-center gap-2">
                    <Info size={14} weight="fill" className="text-fa-yellow" />
                    <span className="font-display font-black text-sm uppercase tracking-tight">How to play · what each call means</span>
                </div>
                {collapsed
                    ? <CaretRight size={12} weight="bold" className="text-white/40" />
                    : <CaretDown size={12} weight="bold" className="text-white/40" />}
            </button>
            {!collapsed && (
                <div className="px-4 pb-4 space-y-2 border-t border-white/5">
                    <p className="font-mono text-[11px] text-white/60 mt-3 leading-relaxed">
                        Before every round you get the <span className="text-fa-yellow font-bold">20 most interesting players</span> of the tournament. For each, decide: buy, keep, sell, or avoid. Score is computed from their actual WC Fantasy points after the round ends.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                        {DECISIONS.map((d) => (
                            <div
                                key={d.key}
                                className="border border-white/10 bg-[#0d0d0d] p-3"
                                data-testid={`help-${d.key}`}
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${d.color}`}>
                                        {d.title}
                                    </span>
                                </div>
                                <p className="font-mono text-[11px] text-white/70 leading-relaxed">{d.body}</p>
                            </div>
                        ))}
                    </div>
                    {deadline && (
                        <p className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow mt-3 pt-2 border-t border-white/5" data-testid="help-deadline">
                            ⏰ Deadline: before the first match of the round — <span className="text-fa-cream">{deadline}</span>
                        </p>
                    )}
                </div>
            )}
        </section>
    );
}
